import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { useSelector } from "react-redux";
import { moduleName } from "./common";
import { getCommentsPayload } from "components/widgets/base/helper";
import { getConfig } from "plugins/config-loader";
import { isLightPortfolioAnalyticsEnabled } from "hooks/use-tenant-settings";
const transformListAnalyses = (response) => ({
    ...response,
    hasLightPortfolioAnalytics: isLightPortfolioAnalyticsEnabled()
});
export const api = createApi({
    baseQuery: fetchBaseQuery({
        baseUrl: `${getConfig("REACT_APP_DASHBOARDS_BASE_URL")}`,
        prepareHeaders: (headers, { getState, extra }) => {
            const state = getState();
            const token = extra.getStateValue(state, "accessToken");
            if (token) {
                headers.set("authorization", `Bearer ${token}`);
            }
            return headers;
        }
    }),
    endpoints: (builder) => ({
        createAnalysis: builder.mutation({
            invalidatesTags: ["Analyses"],
            query: (request) => ({
                body: request,
                method: "POST",
                url: "analysis"
            })
        }),
        createComment: builder.mutation({
            invalidatesTags: ["Comments"],
            query: (request) => ({
                body: request,
                method: "POST",
                url: "comment"
            })
        }),
        createWorkspace: builder.mutation({
            query: (request) => ({
                body: request,
                method: "POST",
                url: "workspace"
            })
        }),
        deleteAnalysis: builder.mutation({
            invalidatesTags: ["Analyses"],
            query: (request) => ({
                method: "DELETE",
                url: `analysis?name=${request.name}`
            })
        }),
        deleteWorkspace: builder.mutation({
            invalidatesTags: ["Workspaces"],
            query: (request) => ({
                method: "DELETE",
                url: `workspace?name=${request.name}`
            })
        }),
        editWorkspace: builder.mutation({
            query: (request) => ({
                body: request,
                method: "PUT",
                url: `workspace/${request.id}`
            })
        }),
        getPage: builder.query({
            query: (request) => ({
                method: "GET",
                url: `page/${request.name}`
            })
        }),
        listAnalyses: builder.query({
            // Keep cached analyses available for a while to avoid refetch on remounts
            keepUnusedDataFor: 300,
            providesTags: ["Analyses"],
            query: (request) => ({
                method: "GET",
                url: `analyses?entityType=${request.entityType}&workspaceId=${request.id}`
            }),
            transformResponse: (response) => transformListAnalyses(response)
        }),
        listComments: builder.query({
            providesTags: ["Comments"],
            query: (request) => ({
                method: "GET",
                url: `comments${getCommentsPayload(request)}`
            })
        }),
        listWorkspacePreferences: builder.query({
            providesTags: ["Workspaces"],
            query: (request) => ({
                method: "GET",
                url: `workspacePreferences?entityType=${request.entityType}`
            })
        }),
        setDefaultWorkspace: builder.mutation({
            invalidatesTags: ["Workspaces"],
            query: (request) => ({
                body: request,
                method: "PUT",
                url: `workspacePreferences/${request.workspaceId}`
            })
        }),
        updateAnalyses: builder.mutation({
            invalidatesTags: ["Analyses"],
            query: (request) => ({
                body: request,
                method: "POST",
                url: "analyses"
            })
        }),
        updateAnalysis: builder.mutation({
            invalidatesTags: ["Analyses"],
            query: (request) => ({
                body: request,
                method: "PUT",
                url: `analysis/${request.id}`
            })
        }),
        updateComment: builder.mutation({
            invalidatesTags: ["Comments"],
            query: (request) => ({
                body: request,
                method: "PUT",
                url: `comment/${request.id}`
            })
        }),
        updateWorkspacePreferences: builder.mutation({
            query: (request) => ({
                body: request,
                method: "POST",
                url: "workspacePreferences"
            })
        })
    }),
    reducerPath: `${moduleName}.api`,
    // Avoid background refetch churn unless explicitly asked by the hook
    refetchOnFocus: false,
    refetchOnReconnect: false,
    tagTypes: ["Workspaces", "Analyses", "Comments"]
});
export const useWorkspacesAPISelector = useSelector;
export const { useCreateAnalysisMutation, useCreateWorkspaceMutation, useDeleteAnalysisMutation, useDeleteWorkspaceMutation, useListWorkspacePreferencesQuery, useEditWorkspaceMutation, useListAnalysesQuery, useUpdateWorkspacePreferencesMutation, useUpdateAnalysesMutation, useUpdateAnalysisMutation, useSetDefaultWorkspaceMutation, useGetPageQuery, useCreateCommentMutation, useListCommentsQuery, useUpdateCommentMutation, middleware: apiMiddleware, reducer: apiReducers, reducerPath: apiReducersPath } = api;
export const workspacePreferencesFetched = api.endpoints.listWorkspacePreferences.matchFulfilled;
export const createdWorkspace = api.endpoints.createWorkspace.matchFulfilled;
export const editedWorkspace = api.endpoints.editWorkspace.matchFulfilled;
export const analysesListFetched = api.endpoints.listAnalyses.matchFulfilled;
export const createdAnalysisFinished = api.endpoints.createAnalysis.matchFulfilled;
export const pageFetched = api.endpoints.getPage.matchFulfilled;
export const analysisUpdated = api.endpoints.updateAnalysis.matchFulfilled;
export const workspacePreferencesUpdated = api.endpoints.updateWorkspacePreferences.matchFulfilled;
export const createdRejectedComment = api.endpoints.createComment.matchRejected;
export const updatedRejectedComment = api.endpoints.updateComment.matchRejected;
